<template>
    <div class="container">
        <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
        <div class="spinner" v-if='page.isLoading'></div>
        <div class="custom-breadcrumb">
            <o-bread-crumb>
                <template v-slot:pageName>
                    User Setting
                </template>
                <template v-slot:pageDetail>
                    Access Token
                </template>
                <template v-slot:pageDescription>
                    <template v-if="isMerchantAccessTokenEnabled">
                        <h5>Personal Access Token</h5>
                        You can generate a personal access token for each application
                        <br> you use that needs access to the Membership API.
                    </template>
                </template>
            </o-bread-crumb>
        </div>
        <div v-if="level.state">
            <div class="alert alert-danger mt-1" role="alert">
                Invalid level, only merchant can create token!
            </div>
        </div>
        <div v-else-if="validation">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>

        <!-- Feature Disabled State -->
        <div v-if="!isMerchantAccessTokenEnabled" class="feature-disabled-container mt-4">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Access Token Disabled</h4>
                <p>The Access Token feature has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isMerchantAccessTokenEnabled">
        <o-card v-if="thisComponentShow" :card-title="'Access Token'" class="custom-table">
            <div class="form-token" v-if="write">
                <div class="create-token">
                    <label class="warning-message">Only users with a merchant level are allowed to create tokens.</label>
                    <o-form-group title="Token Name" name="name">
                        <span class="text-danger"> *</span>
                        <o-input-text v-model="token.name" type="text" :required="true" :autocomplete="'off'" />
                    </o-form-group>

                    <o-button :disabled='page.isLoading || (token.name == "" || token.abilities.length == 0)'
                        type="submit" @click="createAccessToken()" :size="'sm'" :variant="'primary'" :white="'white'">
                        Create personal access token
                    </o-button>
                </div>
                <div class="token-info" v-if="new_token != ''">
                    <o-form-group title="Your personal access token" name="token">
                        <input v-model="new_token" class="form-control" type="text" disabled>
                    </o-form-group>
                    <o-button :disabled='page.isLoading' type="button" @click="copyToClipboard()" :size="'sm'"
                        :variant="'info'" :white="'white'">
                        {{ button.status }}
                    </o-button>
                </div>
            </div>
            <div class="ability_token" v-if="write">
                <o-form-group title="Token Abilities" class="form-check" name="abilities">
                    <span class="text-danger"> *</span>
                    <div class="d-flex">
                        <div class="me-2">
                            <o-check-box name="read" value="read" v-model="token.abilities">Read</o-check-box>
                        </div>
                        <div class="me-2">
                            <o-check-box name="create" value="create" v-model="token.abilities">Create</o-check-box>
                        </div>
                        <div class="me-2">
                            <o-check-box name="update" value="update" v-model="token.abilities">Update</o-check-box>
                        </div>
                        <div class="me-2">
                            <o-check-box name="delete" value="delete" v-model="token.abilities">Delete</o-check-box>
                        </div>
                    </div>
                </o-form-group>
            </div>
            <div class="token-list">
                <o-table style="text-align:left;" class="table-hover">
                    <thead>
                        <tr>
                            <td class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                                {{ field }}
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in tokens" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.abilities.join(", ") }}</td>
                            <td>{{ item.created }}</td>
                            <td>{{ item.last_used }}</td>
                            <td>{{ item.expired }}</td>
                            <td class="ml-3">
                                <button 
                                    v-if="write"
                                    class="btn btn-sm btn-danger rounded-pill text-white" 
                                    :disable="isLoading"
                                    @click.prevent="confirmDelete(item)"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </o-table>
            </div>
        </o-card>
        <o-warning-box
            v-if="isDeleteConfirmVisible"
            title="Delete Access Token"
            :item-name="itemToDelete?.name || 'this token'"
            warning-message="This action cannot be undone."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            :is-loading="page.isLoading"
            @confirm="handleDelete"
            @cancel="closeDeleteConfirm"
        />
        </template>
    </div>
</template>

<script>
import { OCard, OFormGroup, OInputText, OButton, OBreadCrumb, OTable, OCheckBox, OWarningBox } from '@/components/OmnihControl';
import { reactive, ref } from '@vue/reactivity';
import { computed, onMounted } from '@vue/runtime-core';
import { useStore } from 'vuex';
import { watch } from 'vue'

export default {
    components: {
        OCard,
        OBreadCrumb,
        OFormGroup,
        OInputText,
        OButton,
        OTable,
        OCheckBox,
        OWarningBox
    },
    setup() {
        const store = useStore();

        const isMerchantAccessTokenEnabled = computed(() => {
            const flag = store.getters['FeatureFlag/getMerchantAccessTokenFlag'];
            return flag !== 'NONACTIVE';
        });

        const page = reactive({
            isLoading: true,
            component: 'access-token'
        });

        const tokens = computed(() => store.state.AccessToken.tokens);

        const token = reactive({
            name: '',
            abilities: []
        });
        
        const level = reactive({
            state: false,
        });

        const fields = ref([
            "No#",
            "Token Name",
            "Token Abilities",
            "Created",
            "Last Used",
            "Expired Until",
            "Action"
        ]);
        
        let new_token = ref("");
        const validation = ref('');

        let button = reactive({
            status: "Copy to clipboard"
        });

        const thisComponentShow = ref(true);

        const selfLoaded = ref(store.state.Auth.selfLoaded);

        const write = ref(false);

        watch(
            () => store.state.Auth.selfLoaded,
            (newVal) => {
                if (newVal) {
                    selfLoaded.value = newVal;
                    loadTokens();
                }
            }
        );

        onMounted(async () => {
            if (selfLoaded.value) {
                page.isLoading = true;

                try {
                    await loadTokens();
                } catch (error) {
                    console.error('Error loading tokens:', error);
                } finally {
                    page.isLoading = false;
                }
            }
        })

        const loadTokens = () => {
            page.isLoading = true;

            store.dispatch('AccessToken/read')
                .then(() => {
                })
                .catch((err) => {
                    validation.value = err.response.data.message;
                    thisComponentShow.value = false; 
                    console.error(err);
                })
                .finally(() => {
                    page.isLoading = false;
                });

            if (store.state.Auth.currUser.level === 'root') {
                write.value = true;
            } else {
                const userModules = store.state.Auth.userInfo.modules;

                if (userModules) {
                    const matchingModule = userModules.find(module => module.module_name === page.component);
                    write.value = matchingModule ? matchingModule.write : false;
                } else {
                    console.error('userModules is undefined');
                    write.value = false;
                }
            }
        }

        const createAccessToken = () => {
            const data = { ...token };
            
            page.isLoading = true;

            store.dispatch('AccessToken/create', { data })
                .then(res => {
                    const req_token = res.token;
                    new_token.value = req_token;
                    token.name = "";
                    token.abilities = [];
                })
                .catch(err => {
                    console.log(err);
                    if (err.response.data.error === "ACCESS_TOKEN.CREATE.DOMAIN.EXCEPTION.INVALID.LEVEL") {
                        level.state = true;
                    } else if (err.response.data.message && err.response.status === 403) {
                        validation.value = err.response.data.message;
                    }
                })
                .finally(() => {
                    page.isLoading = false;
                    loadTokens();
                });
        }

        const isDeleteConfirmVisible = ref(false);
        const itemToDelete = ref(null);

        const confirmDelete = (item) => {
            itemToDelete.value = item;
            isDeleteConfirmVisible.value = true;
        }

        const closeDeleteConfirm = () => {
            isDeleteConfirmVisible.value = false;
            itemToDelete.value = null;
        }

        const handleDelete = () => {
            if (!itemToDelete.value) return;

            page.isLoading = true;

            store.dispatch('AccessToken/delete', itemToDelete.value.id)
                .catch(err => {
                    console.log(err);
                    if (err.response.data.message && err.response.status === 403) {
                        validation.value = err.response.data.message;
                    }
                })
                .finally(() => {
                    page.isLoading = false;
                    closeDeleteConfirm();
                    loadTokens(); 
                });
        }

        const copyToClipboard = async () => {
            await navigator.clipboard.writeText(new_token.value);
            button.status = "Copied !"

            setTimeout(() => {
                button.status = "Copy to clipboard";
            }, 1500);
        }

        return {
            page,
            token,
            fields,
            tokens,
            new_token,
            button,
            copyToClipboard,
            createAccessToken,
            confirmDelete,
            handleDelete,
            closeDeleteConfirm,
            isDeleteConfirmVisible,
            itemToDelete,
            level,
            validation,
            thisComponentShow,
            write,
            isMerchantAccessTokenEnabled
        }
    },
}
</script>

<style scoped>
.card.custom-table {
    width: 800px;
}

.form-token {
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
}

.form-token .create-token {
    width: 330px;
    margin-right: 10px;
}

.container .form-token button.btn {
    margin-top: 10px !important;
    padding: 0.7rem 3.5rem 0.7rem 3.5rem;
    width: 100%;
}

.container .form-token .token-info input.form-control {
    font-size: 12px !important;
    width: 340px;
    height: 47.2px;
}

.warning-message {
  color: #cb5b5b;
  font-weight: bold;
  font-size: 16px;
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}
</style>